import {
  Box,
  Button,
  Stack,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';

import {
  EventCategory,
  ReminderType,
  RecurringPattern,
  RecurringPeriod,
  ReminderUnit,
} from '@/types/calendar';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { Id } from '@/api/utils/sql';
import { XCloseIcon } from '@/assets/icons/XCloseIcon';
import { useAuth } from '@/context/AuthProvider';

import DocumentViewModal from '@/components/document/DocumentViewModal';
import ModalWithPreview from '@/components/modals/ModalWithPreview';

// Define interface for display events
interface DisplayEvent {
  id: string | number;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  description?: string | null;
  isAiEvent?: boolean;
  external_document_id?: string;
  // Optional CalendarEvent properties
  propertyLocationId?: Id;
  buildingId?: Id;
  unitId?: Id;
  tenantId?: Id | string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  location?: {
    id: Id;
    name: string;
    type: string;
  };
  building?: {
    id: Id;
    name: string;
  };
  unit?: {
    id: Id;
    name: string;
  };
  tenant?: {
    id: Id;
    full_name: string;
  };
  document_metadata?: {
    metadata: {
      data: {
        document: {
          summary: string;
        };
      };
    };
  };
  // Additional fields for enhanced event details
  category?: EventCategory;
  reminderType?: ReminderType;
  recurringPattern?: RecurringPattern;
  recurringEvery?: number;
  recurringPeriod?: RecurringPeriod;
  neverExpires?: boolean;
  reminderIntervals?: Array<{ value: number; unit: ReminderUnit }>;
  notifySelf?: boolean;
  notifyTenant?: boolean;
  notifyVendor?: boolean;
  emailNotification?: boolean;
  smsNotification?: boolean;
  pushNotification?: boolean;
  dashboardNotification?: boolean;
  vendorId?: string;
  vendorEmail?: string;
  vendorMessage?: string;
  // New structure for reminders
  reminders?: Array<{
    id: number;
    calendar_event_id: number;
    remind_at: string;
    type: string;
    unit: string;
    value: number;
    sent: boolean;
    created_at: string;
    updated_at: string;
  }>;
  // New structure for recurring settings
  recurringSettings?: {
    id: number;
    calendar_event_id: number;
    pattern: string;
    every: number;
    period: string;
    end_date: string | null;
    created_at: string;
    updated_at: string;
  };
  // New structure for metadata
  metadata?: {
    category?: string;
    notifySelf?: boolean;
    notifyTenant?: boolean;
    notifyVendor?: boolean;
    smsNotification?: boolean;
    pushNotification?: boolean;
    emailNotification?: boolean;
    dashboardNotification?: boolean;
  };
  property_location?: {
    id: number;
    name: string;
    type: string;
    address_text?: string;
  };
}

// Define component props for viewing event details
interface EventDetailsModalProps {
  event: DisplayEvent;
  onClose: () => void;
  onEditClick?: (event: DisplayEvent) => void;
  onEventUpdated?: () => void;
}

// Helper to render category labels with appropriate styling
const getCategoryLabel = (category?: EventCategory | string) => {
  if (!category) return null;

  // Define category styling config
  const categoryConfig: Record<string, { label: string; color: string }> = {
    lease: { label: 'Lease', color: '#175CD3' },
    payment: { label: 'Payment', color: '#087443' },
    maintenance: { label: 'Maintenance', color: '#B93815' },
    inspection: { label: 'Inspection', color: '#6941C6' },
    other: { label: 'Other', color: '#717680' },
  };

  // Handle both enum values and string values
  const categoryKey =
    typeof category === 'string'
      ? category.toLowerCase()
      : Object.keys(EventCategory)
        .find(key => EventCategory[key as keyof typeof EventCategory] === category)
        ?.toLowerCase();

  // Default to 'other' if category is not recognized
  const categoryInfo =
    categoryKey && categoryConfig[categoryKey]
      ? categoryConfig[categoryKey]
      : { label: 'Other', color: '#717680' };

  return (
    <Chip
      label={categoryInfo.label}
      size="small"
      sx={{
        backgroundColor: categoryInfo.color,
        color: 'white',
        fontWeight: 600,
        fontSize: '12px',
        borderRadius: '16px',
        height: '24px',
        px: 1,
      }}
    />
  );
};

// Helper to get reminder type label
const getReminderTypeLabel = (type?: ReminderType) => {
  if (!type) return null;
  return type === ReminderType.ONE_TIME ? 'One-Time Event' : 'Recurring Event';
};

// Helper to get recurring pattern label
const getRecurringPatternLabel = (
  pattern?: RecurringPattern,
  every?: number,
  period?: RecurringPeriod
) => {
  if (!pattern) return null;

  if (pattern === RecurringPattern.CUSTOM && every && period) {
    return `Every ${every} ${period.replace('_', ' ').toLowerCase()}`;
  }

  const patternLabels: Record<RecurringPattern, string> = {
    [RecurringPattern.DAILY]: 'Daily',
    [RecurringPattern.WEEKLY]: 'Weekly',
    [RecurringPattern.MONTHLY]: 'Monthly',
    [RecurringPattern.YEARLY]: 'Yearly',
    [RecurringPattern.CUSTOM]: 'Custom',
  };

  return patternLabels[pattern];
};

// Helper to format reminder with exact remind_at date
const formatReminderWithExactDate = (remindAt: string) => {
  const reminderDate = new Date(remindAt);
  reminderDate.setUTCHours(12, 0, 0, 0);
  return format(reminderDate, 'MMM d, yyyy');
};

const EventDetailsModal = ({
  event,
  onClose,
  onEditClick,
  onEventUpdated,
}: EventDetailsModalProps) => {
  const [isDocumentViewOpen, setIsDocumentViewOpen] = useState(false);
  const { session } = useAuth();

  const handleDeleteReminder = async (reminderId: number) => {
    try {
      const endpoint = getResolvedEndpoint('DELETE_CALENDAR_EVENT_REMINDER', {
        reminderId: reminderId.toString(),
      });

      await handleApiCall<void, never>(
        session,
        'DELETE',
        endpoint,
        undefined,
        { method: 'DELETE' }
      );

      enqueueSnackbar('Reminder deleted successfully', { variant: 'success' });
      onEventUpdated?.();
    } catch (error) {
      enqueueSnackbar('Failed to delete reminder', { variant: 'error' });
    }
  };

  // Create styles based on Figma design
  const figmaStyles = {
    formSection: {
      p: 1.5,
      mb: 1.5,
      border: '1px solid',
      borderColor: 'grey.200',
      borderRadius: 1,
      boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
      backgroundColor: '#FFFFFF',
    },
    sectionTitle: {
      mb: 1,
      fontWeight: 500,
      fontSize: '14px',
      color: '#414651',
    },
    inputLabel: {
      fontWeight: 500,
      fontSize: '13px',
      color: '#414651',
      mb: 0.5,
    },
    chipStyle: {
      height: '24px',
      fontSize: '12px',
      fontWeight: 500,
    },
    boxSpacing: {
      mb: 1.5,
    },
  };

  return (
    <>
      <ModalWithPreview
        title=""
        onClose={onClose}
        maxWidth="md"
        fullScreen={false}
        showCloseButton={false}
        Actions={
          <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end' }}>
            {!event.isAiEvent && onEditClick && (
              <Button
                variant="contained"
                onClick={() => onEditClick(event)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Edit Event
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: '#D1D5DB',
                color: '#374151',
                '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F3F4F6' },
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Close
            </Button>
          </Stack>
        }
      >
        <Stack spacing={1.5} sx={{ p: 0 }}>
          {/* Title, Date, and Description Section */}
          <Box sx={{ ...figmaStyles.formSection, ...figmaStyles.boxSpacing }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {event.title}
              </Typography>
              {(event.category || event.metadata?.category) &&
                getCategoryLabel(event.category || event.metadata?.category)}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {(() => {
                const startDate = new Date(event.start);
                const endDate = new Date(event.end);
                const isSameDay =
                  startDate.getFullYear() === endDate.getFullYear() &&
                  startDate.getMonth() === endDate.getMonth() &&
                  startDate.getDate() === endDate.getDate();
                if (!isSameDay) {
                  return `${format(startDate, 'EEEE, MMMM d, yyyy')} - ${format(endDate, 'EEEE, MMMM d, yyyy')}`;
                }
                return `${format(startDate, 'EEEE, MMMM d, yyyy')}`;
              })()}
              {!event.all_day && ` • ${format(new Date(event.start), 'h:mm a')} - ${format(new Date(event.end), 'h:mm a')}`}
              {event.all_day && ' • All day'}
            </Typography>
            {event.description && (
              <Typography variant="body1" sx={{ color: '#374151', mb: 2 }}>
                {event.description}
              </Typography>
            )}
            {!event.description && (
              <Typography variant="body2" sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                No description added
              </Typography>
            )}
          </Box>

          {/* Location and Recipients Section */}
          <Box sx={{ display: 'flex', gap: 3, ...figmaStyles.boxSpacing }}>
            {/* Location Box */}
            <Box sx={{ ...figmaStyles.formSection, flex: 1 }}>
              <Typography variant="h6" sx={figmaStyles.sectionTitle}>
                Location
              </Typography>
              {event.property_location && (
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {event.property_location.name}
                  {event.property_location.address_text && ` • ${event.property_location.address_text}`}
                </Typography>
              )}
              {event.updated_at && (
                <>
                  <Typography sx={{ ...figmaStyles.inputLabel, mt: 2 }}>Last Edited</Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {format(new Date(event.updated_at), 'MMM d, yyyy • h:mm a')}
                  </Typography>
                </>
              )}
            </Box>

            {/* Recipients Box */}
            {(
              event.notifySelf || 
              event.notifyTenant || 
              event.notifyVendor ||
              event.metadata?.notifySelf || 
              event.metadata?.notifyTenant || 
              event.metadata?.notifyVendor ||
              event.reminders?.length || 
              event.emailNotification || 
              event.smsNotification ||
              event.pushNotification || 
              event.dashboardNotification ||
              event.metadata?.emailNotification || 
              event.metadata?.smsNotification ||
              event.metadata?.pushNotification || 
              event.metadata?.dashboardNotification
            ) && (
              <Box sx={{ ...figmaStyles.formSection, flex: 1 }}>
                <Typography variant="h6" sx={figmaStyles.sectionTitle}>
                  Recipients & Notifications
                </Typography>
                <Stack spacing={2}>
                  {/* Reminder Type */}
                  {event.reminderType && (
                    <Box>
                      <Typography sx={figmaStyles.inputLabel}>Reminder Type</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                          size="small"
                          label={getReminderTypeLabel(event.reminderType)}
                          sx={{ ...figmaStyles.chipStyle, backgroundColor: '#F3F4F6', color: '#374151' }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Recipients */}
                  <Box>
                    <Typography sx={figmaStyles.inputLabel}>Recipients</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {(event.notifySelf || event.metadata?.notifySelf) && (
                        <Chip
                          size="small"
                          label="Property Manager"
                          sx={{ ...figmaStyles.chipStyle, backgroundColor: '#EEF2FF', color: '#4338CA' }}
                        />
                      )}
                      {(event.notifyTenant || event.metadata?.notifyTenant) && (
                        <Chip
                          size="small"
                          label="Tenant"
                          sx={{ ...figmaStyles.chipStyle, backgroundColor: '#FEF2F2', color: '#B91C1C' }}
                        />
                      )}
                      {(event.notifyVendor || event.metadata?.notifyVendor) && (
                        <Chip
                          size="small"
                          label="Vendor"
                          sx={{ ...figmaStyles.chipStyle, backgroundColor: '#F0FDF4', color: '#15803D' }}
                        />
                      )}
                      {!event.notifySelf &&
                        !event.notifyTenant &&
                        !event.notifyVendor &&
                        !event.metadata?.notifySelf &&
                        !event.metadata?.notifyTenant &&
                        !event.metadata?.notifyVendor && (
                        <Typography variant="body2" sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                          No recipients added
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Reminders */}
                  {event.reminders && event.reminders.length > 0 && (
                    <Box>
                      <Typography sx={figmaStyles.inputLabel}>Reminders</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {event.reminders.map(reminder => (
                          <Chip
                            key={`reminder-${reminder.id}`}
                            label={formatReminderWithExactDate(reminder.remind_at)}
                            size="small"
                            sx={{
                              ...figmaStyles.chipStyle,
                              backgroundColor: '#EFF6FF',
                              color: '#1E40AF',
                            }}
                            deleteIcon={
                              <IconButton
                                size="small"
                                sx={{
                                  ml: -1,
                                  color: '#1E40AF',
                                  '&:hover': { backgroundColor: 'rgba(30, 64, 175, 0.1)' },
                                }}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteReminder(reminder.id);
                                }}
                              >
                                <XCloseIcon width={16} height={16} />
                              </IconButton>
                            }
                            onDelete={() => handleDeleteReminder(reminder.id)}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {(!event.reminders || event.reminders.length === 0) && (
                    <Box>
                      <Typography sx={figmaStyles.inputLabel}>Reminders</Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                        No reminders added
                      </Typography>
                    </Box>
                  )}

                  {/* Notification Methods */}
                  {(event.emailNotification ||
                    event.smsNotification ||
                    event.pushNotification ||
                    event.dashboardNotification ||
                    event.metadata?.emailNotification ||
                    event.metadata?.smsNotification ||
                    event.metadata?.pushNotification ||
                    event.metadata?.dashboardNotification) && (
                    <Box>
                      <Typography sx={figmaStyles.inputLabel}>Notification Methods</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {(event.emailNotification || event.metadata?.emailNotification) && (
                          <Chip
                            size="small"
                            label="Email"
                            sx={{ ...figmaStyles.chipStyle, backgroundColor: '#EEF2FF', color: '#4338CA' }}
                          />
                        )}
                        {(event.smsNotification || event.metadata?.smsNotification) && (
                          <Chip
                            size="small"
                            label="SMS"
                            sx={{ ...figmaStyles.chipStyle, backgroundColor: '#FEF2F2', color: '#B91C1C' }}
                          />
                        )}
                        {(event.pushNotification || event.metadata?.pushNotification) && (
                          <Chip
                            size="small"
                            label="Push"
                            sx={{ ...figmaStyles.chipStyle, backgroundColor: '#F0FDF4', color: '#15803D' }}
                          />
                        )}
                        {(event.dashboardNotification || event.metadata?.dashboardNotification) && (
                          <Chip
                            size="small"
                            label="Dashboard"
                            sx={{ ...figmaStyles.chipStyle, backgroundColor: '#FEF3C7', color: '#92400E' }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                  {!event.emailNotification &&
                    !event.smsNotification &&
                    !event.pushNotification &&
                    !event.dashboardNotification &&
                    !event.metadata?.emailNotification &&
                    !event.metadata?.smsNotification &&
                    !event.metadata?.pushNotification &&
                    !event.metadata?.dashboardNotification && (
                    <Box>
                      <Typography sx={figmaStyles.inputLabel}>Notification Methods</Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                        No notification methods added
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}
          </Box>

          {/* Document Summary */}
          {event.document_metadata?.metadata?.data?.document?.summary && (
            <Box sx={{ ...figmaStyles.formSection, ...figmaStyles.boxSpacing }}>
              <Typography variant="h6" sx={figmaStyles.sectionTitle}>
                Document Summary
              </Typography>
              <Typography sx={{ color: '#374151' }}>
                {event.document_metadata.metadata.data.document.summary}
              </Typography>
            </Box>
          )}

          {/* Document View Button */}
          {event.external_document_id && (
            <Box sx={{ display: 'flex', justifyContent: 'center', ...figmaStyles.boxSpacing }}>
              <Button
                variant="outlined"
                onClick={() => setIsDocumentViewOpen(true)}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                  '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F3F4F6' },
                  fontWeight: 500,
                  px: 3,
                }}
              >
                View Document
              </Button>
            </Box>
          )}

          {/* Recurring Settings */}
          {(event.reminderType === ReminderType.RECURRING || event.recurringSettings) && (
            <Box sx={{ ...figmaStyles.formSection, ...figmaStyles.boxSpacing }}>
              <Typography variant="h6" sx={figmaStyles.sectionTitle}>
                Recurrence
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  size="small"
                  label={event.recurringSettings
                    ? `Every ${event.recurringSettings.every} ${event.recurringSettings.period.toLowerCase()}`
                    : getRecurringPatternLabel(
                      event.recurringPattern,
                      event.recurringEvery,
                      event.recurringPeriod
                    )}
                  sx={{ ...figmaStyles.chipStyle, backgroundColor: '#F3F4F6', color: '#374151' }}
                />
                {(event.neverExpires !== undefined ||
                  event.recurringSettings?.end_date === null) && (
                  <Chip
                    size="small"
                    label={event.neverExpires || event.recurringSettings?.end_date === null
                      ? 'Never expires'
                      : `Ends ${format(
                        new Date(event.recurringSettings?.end_date || event.end),
                        'MMM d, yyyy'
                      )}`}
                    sx={{ ...figmaStyles.chipStyle, backgroundColor: '#F3F4F6', color: '#374151' }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Stack>
      </ModalWithPreview>

      {isDocumentViewOpen && event.external_document_id && (
        <DocumentViewModal
          id={event.external_document_id}
          onClose={() => setIsDocumentViewOpen(false)}
        />
      )}
    </>
  );
};

export default EventDetailsModal;